"use client";

import { useUser } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { toast } from "@/components/ui/sonner";
import { useGetCart } from "@/features/cart/api/hooks/use-get-cart";
import { useRecreateCart } from "@/features/cart/api/hooks/use-recreate-cart";
import { useUpdateCart } from "@/features/cart/api/hooks/use-update-cart";
import { useCartStore } from "@/features/cart/components";
import {
  productItemsForItem,
  toProductItemInputs,
} from "@/features/cart/helpers/product-items";
import { isCartFinalizedError } from "@/features/cart/utils/is-cart-finalized-error";
import {
  getUnavailableProductId,
  isUnavailableProductError,
} from "@/features/cart/utils/unavailable-product-error";
import { usePendingPayment } from "@/features/checkout/hooks/use-pending-payment";

export const CartSyncHandler = () => {
  const t = useTranslations();
  const { isLoaded, isSignedIn } = useUser();
  const queryClient = useQueryClient();

  const agreements = useCartStore((state) => state.agreements);
  const cartId = useCartStore((state) => state.cartId);
  const discountCode = useCartStore((state) => state.discountCode);
  const invoiceData = useCartStore((state) => state.invoiceData);
  const items = useCartStore((state) => state.items);
  const productItems = useCartStore((state) => state.productItems);
  const resetCart = useCartStore((state) => state.resetCart);
  const setCart = useCartStore((state) => state.setCart);
  const setCartId = useCartStore((state) => state.setCartId);

  const { mutate: recreateCart } = useRecreateCart();
  const { mutate: updateCart } = useUpdateCart();
  const pendingPayment = usePendingPayment();
  const { data: cartData, error: cartError } = useGetCart({
    id: cartId || "",
    options: {
      enabled: !!cartId,
      // A finalized cart and one blocked by a product without a price are
      // handled below, retrying would only delay that.
      retry: (failureCount, error) =>
        !isCartFinalizedError(error) &&
        !isUnavailableProductError(error) &&
        failureCount < 3,
    },
  });

  const handledFinalizedCartIdRef = useRef<string | null>(null);
  const handledUnavailableProductRef = useRef<string | null>(null);

  // Since MPL-3829 GET /cart answers 422 `product_items_not_priced` when a
  // Foodango product in the cart left the diet's offer, and stays unreadable
  // until that entry is dropped. Rewrite the position the product rode with,
  // without it; the update route rebuilds the rest of the cart from the copy
  // this client holds, so nothing else is lost.
  useEffect(() => {
    const productId = getUnavailableProductId(cartError);
    if (!(cartId && productId)) {
      return;
    }

    const key = `${cartId}:${productId}`;
    if (handledUnavailableProductRef.current === key) {
      return;
    }
    handledUnavailableProductRef.current = key;

    const product = productItems?.find(
      ({ productItem }) => productItem.productId === productId,
    );
    const parent = items.find(
      (item) =>
        item.configurationItem.id === product?.productItem.configurationItemId,
    );
    if (!(product && parent)) {
      // Nothing local to drop it from: the copy predates the product.
      return;
    }

    const remaining = productItemsForItem(
      productItems,
      parent.configurationItem.id,
    )
      .filter(({ productItem }) => productItem.productId !== productId)
      .map(({ productItem }) => ({
        productId: productItem.productId,
        merchantId: productItem.merchantId,
        date: productItem.date,
        quantity: productItem.quantity,
      }));

    updateCart(
      { cartId, item: parent.configurationItem, productItems: remaining },
      {
        onSuccess: ({ cart, cartId: nextCartId }) => {
          queryClient.setQueryData(["cart", nextCartId], cart);
          setCartId(nextCartId);
          setCart(cart);
          toast.info(t("cart.addons.errors.productRemoved"));
        },
      },
    );
  }, [
    cartError,
    cartId,
    items,
    productItems,
    queryClient,
    setCart,
    setCartId,
    t,
    updateCart,
  ]);
  const lastSyncKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !cartId) return;

    const syncKey = `${cartId}:${isSignedIn ? "auth" : "guest"}`;

    if (lastSyncKeyRef.current === syncKey) return;
    lastSyncKeyRef.current = syncKey;

    queryClient.invalidateQueries({
      queryKey: ["cart", cartId],
    });
  }, [cartId, isLoaded, isSignedIn, queryClient]);

  useEffect(() => {
    if (!(cartId && isCartFinalizedError(cartError))) {
      return;
    }

    if (handledFinalizedCartIdRef.current === cartId) {
      return;
    }

    // The cart is finalized because the user started a payment for it (the
    // classic order id is the cart id). While that payment is unsettled the
    // return flow decides what happens to the cart: COMPLETED resets it,
    // CANCELED/REJECTED clears the pending payment, which re-runs this effect
    // and recreates it. Recreating it now would race the reset and bring the
    // bought diets back into the cart.
    if (pendingPayment?.orderId === cartId) {
      return;
    }

    handledFinalizedCartIdRef.current = cartId;

    if (items.length === 0) {
      resetCart();
      queryClient.removeQueries({ exact: true, queryKey: ["cart", cartId] });
      return;
    }

    const finalizedCartId = cartId;

    recreateCart(
      {
        agreements,
        discountCode,
        invoiceData,
        items: items.map((item) => item.configurationItem),
        productItems: toProductItemInputs(productItems),
      },
      {
        onSuccess: ({ cart, cartId: recreatedCartId }) => {
          queryClient.setQueryData(["cart", recreatedCartId], cart);
          setCartId(recreatedCartId);
          setCart(cart);
          queryClient.removeQueries({
            exact: true,
            queryKey: ["cart", finalizedCartId],
          });
        },
      },
    );
  }, [
    agreements,
    cartError,
    cartId,
    discountCode,
    invoiceData,
    items,
    pendingPayment?.orderId,
    productItems,
    queryClient,
    recreateCart,
    resetCart,
    setCart,
    setCartId,
  ]);

  useEffect(() => {
    if (!cartData) return;
    setCart(cartData);
  }, [cartData, setCart]);

  return null;
};